import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    LOCALE_ID,
    provideAppInitializer
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideHttp } from './core/http/http.provider';
import { MockApiService } from './mock-api';
import { FullCalendarModule } from '@fullcalendar/angular';
import localeDeCH from '@angular/common/locales/de-CH';
import { registerLocaleData } from '@angular/common';
import { LoaderInterceptor } from './core/http/load.interceptor';
import { FILE_BASE_URL } from './shared/helpers/image-base.token';
import { environment } from './environments/environment';
import { layoutKey, schemeKey, themeKey } from './layout/common/settings/settingsStorageKeys';
import { Scheme } from '@fuse/services/config';
import { CustomMatPaginatorIntl } from './shared/services/custom-mat-paginator-intl.service';

const DEFAULT_LANGUAGE = 'de';
const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it'];

function getEmbeddedLanguage(): string | null {
    const language = new URLSearchParams(window.location.search).get('lang');
    if (!language) {
        return null;
    }

    const normalizedLanguage = language.toLowerCase().split('-')[0];
    return SUPPORTED_LANGUAGES.includes(normalizedLanguage) ? normalizedLanguage : null;
}

registerLocaleData(localeDeCH);

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
        ),
        { provide: LOCALE_ID, useValue: 'de-CH' },
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        // Material Paginator Internationalization
        {
            provide: MatPaginatorIntl,
            useClass: CustomMatPaginatorIntl,
        },
        { provide: FILE_BASE_URL, useValue: environment.fileUrl },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'de',
                        label: 'Deutsch',
                    },
                    {
                        id: 'fr',
                        label: 'Français',
                    },
                    {
                        id: 'it',
                        label: 'Italiano',
                    },
                ],
                defaultLang: DEFAULT_LANGUAGE,
                fallbackLang: DEFAULT_LANGUAGE,
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
                scopes: {
                    keepCasing: false,
                }
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const storedLanguage = localStorage.getItem('lang');
            const embeddedLanguage = getEmbeddedLanguage();
            const activeLanguage = embeddedLanguage
                ?? (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage)
                    ? storedLanguage
                    : translocoService.getDefaultLang());

            localStorage.setItem('lang', activeLanguage);
            translocoService.setActiveLang(activeLanguage);

            return firstValueFrom(translocoService.load(activeLanguage));
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: localStorage.getItem(layoutKey) ?? 'classy',
                scheme: localStorage.getItem(schemeKey) as Scheme ?? 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: localStorage.getItem(themeKey) ?? 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),

        // app 
        provideHttp(),
        FullCalendarModule,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,
            multi: true,
        }
    ],
};

